import { Bot, User, Wrench, ShieldCheck, Paperclip, FileVideo, FileImage, FileText } from "lucide-react"
import { cn } from "@/lib/cn"
import { ToolCallChip } from "@/components/domain/tool-call-chip"
import { MessageMarkdown } from "@/components/domain/message-markdown"
import type { ChatBubble } from "@/types/test_lab"

type Props = {
  msg: ChatBubble
  /** Which side the user role lands on. Test tab → "right" (operator-as-user). */
  userSide?: "left" | "right"
  userLabel?:      string
  assistantLabel?: string
  /** Render the tool_call / tool_result chips below the bubble.
   *  Defaults to true (designer + test panes need them); the production
   *  ticket viewer flips this off for non-admin operators. */
  showToolCalls?:  boolean
}

// One message turn in the support-inbox style.
//
// Layout (inline / Zendesk-style — every role flows down a single column):
//   [avatar] Name · time
//            ┌──────────────────┐
//            │ content          │
//            └──────────────────┘
//            ▸ tool_call chip
//
// Avatar always anchors on the left. The `userSide` prop is kept on the
// type for back-compat with existing callsites, but is no longer read —
// the inline layout doesn't have a "right side" anymore.
export function ChatMessageBubble({
  msg,
  userSide: _userSide,
  userLabel = "You (as user)",
  assistantLabel = "Agent",
  showToolCalls = true,
}: Props) {
  void _userSide
  const isUser      = msg.role === "user"
  const isAssistant = msg.role === "assistant"
  const isHuman     = msg.role === "human_agent"
  const isSystem    = msg.role === "system"
  const isTool      = msg.role === "tool"
  const isInternal  = msg.role === "internal_note"

  const label =
    msg.authorName?.trim()
      ? msg.authorName
      : isUser ? userLabel
      : isAssistant ? assistantLabel
      : isHuman ? "Human agent"
      : isSystem ? "System"
      : isTool ? "Tool"
      : isInternal ? "Internal note"
      : msg.role

  const Icon = isUser ? User : isHuman ? User : isTool ? Wrench : isSystem || isInternal ? ShieldCheck : Bot

  // Avatar tint: red for operator-as-user / human, gold for agent, neutral else.
  const avatarTone = isUser || isHuman
    ? "border-brand-red/40 bg-brand-redSoft text-brand-red"
    : isAssistant
      ? "border-vip-gold/40 bg-vip-goldMute text-vip-goldSoft"
      : isInternal
        ? "border-status-warning/40 bg-status-warning/10 text-status-warning"
        : "border-border bg-app-panel2 text-app-muted"

  // Initial used by the letter-tile fallback when no avatar URL is supplied.
  const initial = (label?.toString().trim()[0] ?? "?").toUpperCase()

  // Bubble: operator gets a red left edge; human a gold edge; agent stays neutral.
  const bubbleTone = isHuman
    ? "border-l-2 border-l-vip-gold/60 border-y border-r border-border bg-app-panel2 text-app-text"
    : isUser
      ? "border-l-2 border-l-brand-red/40 border-y border-r border-border bg-app-panel2 text-app-text"
      : isAssistant
        ? "border border-border bg-app-panel2 text-app-text"
        : isInternal
          ? "border border-status-warning/35 bg-status-warning/10 text-app-text"
          : "border border-border bg-white/[0.03] text-app-muted"

  const time = new Date(msg.createdAt).toLocaleTimeString([], {
    hour: "numeric", minute: "2-digit",
  })

  const toolCalls   = Array.isArray(msg.toolCalls)   ? msg.toolCalls   as Array<Record<string, unknown>> : []
  const toolResults = Array.isArray(msg.toolResults) ? msg.toolResults as Array<Record<string, unknown>> : []
  const files       = Array.isArray(msg.files)       ? msg.files                                           : []

  // Three-tier avatar: real photo URL → letter-tile (when we have a name to
  // derive a letter from) → role icon fallback. The outer wrapper keeps the
  // 36×36 footprint identical across all three so the column doesn't jump.
  const Avatar = msg.avatarUrl ? (
    <img
      src={msg.avatarUrl}
      alt={initial}
      loading="lazy"
      className="h-12 w-12 shrink-0 rounded-full border border-border bg-app-panel2 object-cover"
    />
  ) : msg.authorName ? (
    <div
      className={cn(
        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full border text-base font-semibold",
        avatarTone,
      )}
    >
      {initial}
    </div>
  ) : (
    <div
      className={cn(
        "flex h-12 w-12 shrink-0 items-center justify-center rounded-full border",
        avatarTone,
      )}
    >
      <Icon size={20} strokeWidth={1.8} />
    </div>
  )

  return (
    <div className="flex w-full gap-3">
      {Avatar}
      <div className="flex max-w-[640px] min-w-0 flex-col gap-1.5">
        <div className="flex items-baseline gap-2">
          <span className="text-xs font-semibold text-app-text">{label}</span>
          <span className="text-[11px] text-app-subtle">{time}</span>
        </div>
        {msg.content && (
          <div className={cn("rounded-card px-4 py-2.5 text-sm", bubbleTone)}>
            {!isTool ? (
              <MessageMarkdown content={msg.content} neutralizeLinks={isSystem} />
            ) : (
              <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
            )}
          </div>
        )}
        {showToolCalls && (toolCalls.length > 0 || toolResults.length > 0) && (
          <div className="w-full space-y-1.5">
            {pairCallsWithResults(toolCalls, toolResults).map((pair, i) => (
              <ToolCallChip
                key={`tool-${i}`}
                toolCall={pair.call}
                toolResult={pair.result}
                timestamp={msg.createdAt}
              />
            ))}
          </div>
        )}
        {files.length > 0 && (
          <div className="flex w-full flex-wrap gap-1.5">
            {files.map((f, i) => (
              <FileChip key={`file-${i}`} file={f} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Pair each tool_call with its matching tool_result (same `id`, the
// Anthropic tool_use_id). Calls without a result render as in-flight;
// orphan results (legacy data with no call alongside) still render so
// nothing is lost. Order follows the call list, then any leftover
// orphan results.
type ToolCall   = { id?: string; name?: string; input?: unknown }
type ToolResult = { id?: string; name?: string; output?: unknown; is_error?: boolean }

// Compact attachment chip rendered inline under a message bubble. We
// show the icon (matched to the file's MIME family), the filename, and
// the size + content_type as a sub-line. The chip is a link when a URL
// is present (real ActiveStorage blob); test fixtures with no URL show
// as a plain disabled-looking chip — useful for confirming that the
// scenario's attachment_fixture actually delivered the file.
function FileChip({ file }: { file: NonNullable<ChatBubble["files"]>[number] }) {
  const mime = file.content_type || ""
  const Icon =
    mime.startsWith("video/") ? FileVideo :
    mime.startsWith("image/") ? FileImage :
    mime.startsWith("text/") || mime === "application/json" ? FileText :
    Paperclip
  const sizeLabel = formatBytes(file.byte_size)
  const meta = [file.content_type, sizeLabel].filter(Boolean).join(" · ")

  const inner = (
    <>
      <Icon size={14} className="shrink-0 text-app-muted" />
      <div className="min-w-0">
        <div className="truncate text-[12px] font-medium text-app-text">{file.filename}</div>
        {meta && <div className="truncate text-[10px] text-app-subtle">{meta}</div>}
      </div>
    </>
  )

  const className = "flex max-w-[260px] items-center gap-2 rounded-control border border-border bg-white/[0.04] px-2.5 py-1.5"
  if (file.url) {
    return (
      <a href={file.url} target="_blank" rel="noopener noreferrer" className={cn(className, "hover:bg-white/[0.07]")}>
        {inner}
      </a>
    )
  }
  return <div className={className} title="Test fixture — no URL available">{inner}</div>
}

function formatBytes(n: number | null | undefined): string {
  if (n == null || n <= 0) return ""
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function pairCallsWithResults(
  calls: Array<Record<string, unknown>>,
  results: Array<Record<string, unknown>>,
): Array<{ call?: ToolCall; result?: ToolResult }> {
  const remainingResults = [...results] as ToolResult[]
  const pairs: Array<{ call?: ToolCall; result?: ToolResult }> = []

  for (const c of calls as ToolCall[]) {
    let matchIdx = -1
    if (c.id) matchIdx = remainingResults.findIndex((r) => r.id === c.id)
    if (matchIdx === -1) matchIdx = remainingResults.findIndex((r) => r.name === c.name)
    const match = matchIdx >= 0 ? remainingResults.splice(matchIdx, 1)[0] : undefined
    pairs.push({ call: c, result: match })
  }
  // Any results without a matching call (split-message convos) tail the list.
  for (const orphan of remainingResults) pairs.push({ result: orphan })

  return pairs
}
